import { cookieManager, UniversalStorage } from "utils";
import type {
  A9HemSyncerConfig,
  ApstagTokenConfig,
  ApstagTokenRenewalAndUpdateFunctions,
  A9ConsentParams,
} from "./a9";
import type { FandomCmp } from "common-types";
import { getAdsContext } from "../../helpers/adsContext/getAdsContext";

/**
 * A9HemSyncer is responsible for managing the synchronization of Hashed Emails (HEM)
 * with Amazon Publisher Services (APS). It handles consent updates, token renewal,
 * and cleanup of A9 tokens stored in cookies and localStorage.
 */
export class A9HemSyncer {
  private readonly AMZN_TOKEN_NAME = "AMZN-Token";
  private readonly APSTAG_RECORD_KEY = "apstagRecord";
  private readonly APSTAG_HEM_SENT_KEY = "apstagHEMsent";
  private readonly APSTAG_HEM_OPTOUT_KEY = "apstagHEMoptOut";
  private readonly AMAZON_TOKEN_TTL = 14 * 24 * 60 * 60 * 1000; // 14 days

  private readonly storage: UniversalStorage = new UniversalStorage();

  constructor(
    private readonly config: A9HemSyncerConfig,
    private consentData: FandomCmp,
  ) {}

  /**
   * Updates the consent data used by the syncer.
   * Also calls function responsible for syncing the user's HEM,
   * through the Amazon Publisher Audiences apis.
   * @param consentData - Updated raw consent data.
   * @param a9ConsentParams - Updated consent data structured for A9.
   */
  updateConsentData(
    consentData: FandomCmp,
    a9ConsentParams: A9ConsentParams,
  ): void {
    this.consentData = consentData;

    this.updateApstagPublisherAudienceConfig(a9ConsentParams);
  }

  /**
   * Updates the APS tag configuration with publisher audience data.
   * If there is no hashedEmail present, then no updates occur.
   * @param apstag - APS tag object.
   * @param gdprParam - GDPR-related consent parameters.
   */
  updateApstagPublisherAudienceConfig({ gdprParam }: A9ConsentParams) {
    this.cleanUpA9TokensIfApplicable();

    if (!this.isHemSyncingEnabled()) {
      return;
    }

    const hashedEmail = this.resolveHashedEmailSource();
    const { optOut } = this.consentData;
    if (!hashedEmail) {
      console.warn("Can't update HEM without an email record.");
      /* If the user doesn't have a hashed email stored, but they've decided to optOut,
       * let's make sure to do a cleanup, in case they had a AMZN-Token store previously.
       * Since the user is specifically opting out, we want to do this cleanup here,
       * otherwise the cleanup could only happen if they're logged in or had previously
       * stored hashed email.*/
      if (optOut && window.apstag) {
        window.apstag.dpa?.();
      }
      /* Don't proceed with any HEM syncing, since the user did not have a hashed email saved.*/
      return;
    }

    const tokenConfig: ApstagTokenConfig = {
      ...gdprParam,
      optOut,
      hashedRecords: [{ type: "email", record: hashedEmail }],
    };

    this.syncHemIfApplicable(tokenConfig);
  }

  /**
   * Retrieves the user's (sha256 encrypted) hashed email stored from localStorage.
   * @returns The stored user's hashed or null if not found.
   */
  private getApstagRecordFromStorage(): string | null {
    return this.storage.getItem(this.APSTAG_RECORD_KEY);
  }

  /**
   * Retrieves the hashed email from the ads context.
   * @returns The SHA-256 encrypted hashed email or null if not available.
   */
  private getHashedEmailFromAdsContext(): string | null {
    const userEmailHashes = getAdsContext()?.opts.userEmailHashes;
    // Make sure there are exactly three entries in the userEmailHashes.
    // This allows us to correctly select the third entry, which is the sha-256 encrypted user email.
    if (!Array.isArray(userEmailHashes) || userEmailHashes?.length !== 3) {
      return null;
    }
    /* The third entry in the userEmailHashes is the sha256 encrypted user email.
     * These sha256 hashed email are also known as HEMs, or Hashed E-Mails.
     * See https://fandom.atlassian.net/wiki/spaces/TACO/pages/2808610927/Glossary for more info.
     * We send this hashed (sha-256) email to A9 on the Amazon Publisher Audience functions (rpa|upa) */
    return userEmailHashes[2];
  }

  /**
   * Resolves the source of the hashed email, prioritizing stored data.
   * @returns The hashed email or null if not available.
   */
  private resolveHashedEmailSource() {
    const storedHashedEmailRecord = this.getApstagRecordFromStorage();
    return (
      storedHashedEmailRecord ?? this.getHashedEmailFromAdsContext() ?? null
    );
  }

  /**
   * Synchronizes the HEM with APS if applicable based on consent and token state.
   * @param tokenConfig - Configuration for the APS token.
   */
  private syncHemIfApplicable(tokenConfig: ApstagTokenConfig) {
    const hashedEmail = tokenConfig?.hashedRecords?.[0]?.record;
    const { optOut } = tokenConfig;
    if (!hashedEmail) return;

    // Stores which apstag Publisher Audience function should be called, based on the AMZN-Token validation and user consent
    const apstagTokenizationFunction =
      this.deriveApstagTokenizationFunction(optOut);

    if (apstagTokenizationFunction && window.apstag) {
      window.apstag[apstagTokenizationFunction]?.(tokenConfig, () => {
        this.updateApstagConsentInLocalStorage(hashedEmail, optOut);
      });
    }
  }

  private deriveApstagTokenizationFunction(
    optOut: boolean,
  ): ApstagTokenRenewalAndUpdateFunctions | null {
    return this.checkIfUserConsentChanged(optOut)
      ? "upa"
      : this.checkIfA9TokenShouldBeRenewed()
        ? "rpa"
        : null;
  }

  private checkIfA9TokenShouldBeRenewed() {
    const expiration = this.storage.getItem(
      this.APSTAG_HEM_SENT_KEY,
      true,
    ) as string;
    // If we never stored the expiration data, then let's renew/generate a new AMZN-Token.
    if (!expiration) {
      return true;
    }

    // Check if the token expired.
    const parsedExpiration = parseInt(expiration, 10);
    // If there were any issues with parsing the expiration date from localStorage,
    // let's just create a new token. This will add new data into the localStorage.
    if (isNaN(parsedExpiration)) {
      return true;
    } else {
      return parsedExpiration < Date.now();
    }
  }

  /**
   * Checks if the A9 token should be renewed based on its expiration date.
   *
   * This method retrieves the expiration timestamp of the A9 token from localStorage.
   * If the expiration timestamp is not found or is invalid, it assumes the token needs renewal.
   * If the expiration timestamp exists, it checks whether the token has expired by comparing
   * the stored timestamp with the current date and time.
   *
   * @returns {boolean} - Returns `true` if the token should be renewed, otherwise `false`.
   */
  private checkIfUserConsentChanged(optOut: boolean): boolean {
    const apsTagOptOutValue = this.storage.getItem(
      this.APSTAG_HEM_OPTOUT_KEY,
      // Retrieve the optOut value as a parsed (non-raw) value, so that the string representation of the boolean value
      // will be parsed back into
      false,
    ) as boolean;
    return apsTagOptOutValue !== null && optOut !== apsTagOptOutValue;
  }

  /**
   * Determines whether HEM (Hashed Emails) syncing is enabled for A9's APS (Amazon Publisher Services).
   *
   * HEM syncing is enabled if the `icA9HEM` configuration flag is `true` and the `icA9CleanHEM` flag is `false`.
   *
   * @returns - Returns `true` if HEM syncing is enabled, otherwise `false`.
   */
  isHemSyncingEnabled(): boolean {
    return this.config.icA9HEM && !this.config.icA9CleanHEM;
  }

  private updateApstagConsentInLocalStorage(
    hashedEmail: string,
    optOut: boolean,
  ) {
    // Store the hashed email, no matter what
    this.storage.setItem(this.APSTAG_RECORD_KEY, hashedEmail);
    // Store the user's optOut value, no matter what.
    this.storage.setItem(
      this.APSTAG_HEM_OPTOUT_KEY,
      // LocalStorage requires a string as a value or an object
      optOut.toString(),
    );
    // Update the expiration date, only when the AMZN-Token is present.
    // If a token wasn't created, then we'll at least be able to create one
    // later on if the expiration isn't set in localStorage.
    if (cookieManager.get(this.AMZN_TOKEN_NAME)) {
      this.storage.setItem(
        this.APSTAG_HEM_SENT_KEY,
        (Date.now() + this.AMAZON_TOKEN_TTL).toString(),
      );
    } else {
      // If the AMZN-Token was removed because of am optOut update,
      // then let's remove the expiration date from localStorage, to
      // later allow a new token to be created through apstag.rpa.
      // Calling apstag.upa with an updated optOut value
      // may delete the AMZN-Token cookie.
      this.storage.removeItem(this.APSTAG_HEM_SENT_KEY);
    }
  }

  cleanUpA9TokensIfApplicable() {
    if (this.config.icA9CleanHEM) {
      this.cleanUpA9Tokens();
    }
  }

  /**
   * Cleans up all the A9 Tokens that are stored in cookies and localStorage.
   * This will also call the {@link ApsTag.dpa} from {@link window.apstag} object.*/
  cleanUpA9Tokens() {
    if (
      cookieManager.get(this.AMZN_TOKEN_NAME) ||
      this.storage.getItem(this.APSTAG_RECORD_KEY)
    ) {
      window.apstag?.dpa?.();
      this.storage.removeItem(this.APSTAG_RECORD_KEY);
      this.storage.removeItem(this.APSTAG_HEM_SENT_KEY);
      this.storage.removeItem(this.APSTAG_HEM_OPTOUT_KEY);
      /* There is no domain set when manually deleting the AMZN-Token
       * cookie, since the domain is not set by apstag. If the domain is passed in,
       * to the cookie removal function, then this cookie will not be deleted.
       *
       * Ideally, window.apstag.dpa() will remove this cookie, but in case apstag
       * is not initialized, then manually deleting this cookie will delete it. */
      cookieManager.delete(this.AMZN_TOKEN_NAME, {
        path: "/",
      });
    }
  }
}
